import { Controller } from '@hotwired/stimulus';
import $ from 'jquery';
import { onPasteSanitize } from '../js/summernote_utils';

/*
 * Stimulus controller for Summernote-enabled textarea fields.
 * Handles initialization, character counting, and content synchronization.
 */
export default class extends Controller {
    static targets = [
        'textarea',
        'counter',
    ];

    connect() {
        this.initializeSummernote();
    }

    disconnect() {
        if ($(this.textareaTarget).data('summernote')) {
            $(this.textareaTarget).summernote('destroy');
        }
    }

    initializeOnFocus() {
        if (!$(this.textareaTarget).data('summernote')) {
            this.initializeSummernote();
        }
    }

    /**
     * Initialize the Summernote editor with configuration
     */
    initializeSummernote() {
        this.focused = false
        const rows = parseInt(this.textareaTarget.getAttribute('rows')) || 5
        const height = rows * 20
        const noToolbar = this.textareaTarget.hasAttribute('data-no-toolbar')

        $(this.textareaTarget).summernote({
            height,
            toolbar: noToolbar ? [] : [
                ['style', ['bold', 'italic', 'underline']],
                ['para', ['ul', 'ol', 'paragraph']],
            ],
            placeholder: this.textareaTarget.placeholder || '',
            disableResizeEditor: true,
            callbacks: {
                onPaste: onPasteSanitize,
                onChange: (contents) => {
                    const isEmpty = $(this.textareaTarget).summernote('isEmpty')
                    this.textareaTarget.value = isEmpty ? '' : contents;
                    this.dirty = true;

                    if (this.hasCounterTarget) {
                        this.updateCharacterCounter();
                    }

                    this.clearValidationError();
                    this._syncPlaceholderVisibility()
                },
                onFocus: () => {
                    this.focused = true
                    $(this.element).find('.note-placeholder').hide();
                },
                onBlur: () => {
                    this.focused = false
                    if ($(this.textareaTarget).summernote('isEmpty')) {
                        $(this.element).find('.note-placeholder').show();
                    }
                },
            },
        });

        $(this.element).find('.note-btn').removeAttr('title').tooltip('dispose');

        const editable = $(this.element).find('.note-editable')
        editable.css({ 'max-height': `${height}px`, 'overflow-y': 'auto' })

        const editingArea = $(this.element).find('.note-editing-area')
        editingArea.on('click', (e) => {
            if (!$(e.target).closest('.note-editable').length) {
                $(this.textareaTarget).summernote('focus')
            }
        })

        $(this.element).find('.note-editor').on('click', () => {
            $(this.textareaTarget).summernote('focus')
        })

        if (this.hasCounterTarget) {
            this.updateCharacterCounter();
        }
    }

    _syncPlaceholderVisibility() {
        // Summernote's Placeholder module re-shows the placeholder on every
        // 'summernote.change' event AFTER our onChange callback runs.
        // queueMicrotask ensures we override it after Summernote's handler.
        queueMicrotask(() => {
            if (this.focused) {
                $(this.element).find('.note-placeholder').hide()
            }
        })
    }

    syncToLiveComponent() {
        if (!this.dirty) {
            return;
        }
        this.dirty = false
        this.textareaTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }

    /**
     * Validate minimum length requirement
     */
    validateMinLength() {
        const minLength = parseInt(this.textareaTarget.getAttribute('minlength'));
        
        if (!minLength) {
            return;
        }
        
        const content = $(this.textareaTarget).summernote('code').replace(/<[^>]*>/g, '').trim();
        const length = content.length;
        
        if (length > 0 && length < minLength) {
            this.textareaTarget.setCustomValidity(`Minimum ${minLength} characters required. Current: ${length}`);
        } else {
            this.textareaTarget.setCustomValidity('');
        }
    }

    /**
     * Update character counter display
     */
    updateCharacterCounter() {
        const length = $(this.textareaTarget).summernote('code').replace(/<[^>]*>/g, '').length;

        this.counterTarget.textContent = `(${length})`;

        if (length > 0) {
            this.counterTarget.classList.remove('d-none');
        }
    }

    clearValidationError() {
        const plainText = $(this.textareaTarget).summernote('code').replace(/<[^>]*>/g, '').trim()
        const minLength = parseInt(this.textareaTarget.getAttribute('minlength'))
        const isSatisfied = minLength ? plainText.length >= minLength : plainText.length > 0

        if (!isSatisfied) {
            return
        }

        const errorWrapper = this.element.querySelector('.invalid-feedback')

        if (errorWrapper) {
            errorWrapper.remove()
        }
    }
}
